import Ishikawa from 'components/Ishikawa'
import { IIshikawaDiagram } from 'components/Ishikawa/types'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import { NotificationAction, NotificationReducer } from 'types/notification'
import NotificationActions from 'actions/Notification'
import { Dispatch } from 'redux'
import { EditButton, TitleIshikawaDiagram, checkInput } from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/IshikawaDiagram/styles'
import { ROUTES_NAMES } from 'constants/name.routes'
import NotificationService, { normalizeIshikawaApiResponseBody } from 'services/Notification'
import { MdOutlineModeEditOutline } from 'react-icons/md'
import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { UserDataType } from 'types/autentication'
import { notificationStatus } from 'constants/notificationStatus'
import { toBoolean } from 'helper/string'
import {
    buildIshikawaSysIdByNotificationMap,
    calculateGroupIshikawaMandatory,
    canEditIshikawaPerfil,
    convertNotPerformIshikawaCreateBatchForNotifications,
    convertNotPerformIshikawaUpdateBatchRequest,
    getIshikawaRecordId,
    mergeFirstIshikawaIdFromRedux,
    partitionNotificationsByProtocolMap,
    GroupData,
    GroupNotificationItem
} from './helpers'

export interface GroupIshikawaDiagramProps {
    id: string
    notificationById: any
    statusIsPendingFinishOrFinished: boolean
    currentUser: UserDataType
    notificationStatusState: string
    tooltipAlert: string
    groupData?: GroupData
    loggedInUserIsResponsible?: boolean
}

const GroupIshikawaDiagram = ({
    id,
    statusIsPendingFinishOrFinished: statusIsArchivedOrFinished,
    currentUser,
    notificationById,
    notificationStatusState,
    tooltipAlert: _tooltipAlertShownInParent,
    groupData,
    loggedInUserIsResponsible = true
}: GroupIshikawaDiagramProps) => {
    const navigate = useNavigate()

    const notificationDispatch: Dispatch<NotificationAction> = useDispatch()

    const notificationSelector: NotificationReducer = useSelector((state: RootReducerState) => state.notificationsReducer)

    // Usar primeira notificação como referência
    const firstNotification = useMemo(() => {
        return groupData?.notifications?.[0]
    }, [groupData])

    const ishikawaDiagramUseMemo = useMemo((): IIshikawaDiagram => {
        const raw = notificationSelector?.ishikawaDiagram
        const flat = normalizeIshikawaApiResponseBody(raw) ?? raw
        const base: Record<string, any> =
            typeof flat === 'object' && flat !== null && !Array.isArray(flat)
                ? { ...(flat as Record<string, any>) }
                : {}
        const categories = Array.isArray(base.categories) ? base.categories : []
        return {
            ...base,
            categories,
            problem:
                firstNotification?.u_reclassified_incident_type?.display_value ||
                notificationById?.u_reclassified_incident_type?.display_value ||
                base.problem ||
                ''
        } as IIshikawaDiagram
    }, [notificationSelector?.ishikawaDiagram, firstNotification, notificationById])

    const [isDisabled, setIsDisabled] = useState(false)
    const [notAllowed, setNotAllowed] = useState(true)

    // Validações baseadas no grupo (group_info)
    const isSameLocation = useMemo(() => {
        // Para grupos, se não temos group_info ainda, assumir true (validação no backend)
        if (!groupData?.group_info) return true
        return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === groupData.group_info.u_location?.value).length > 0
    }, [currentUser, groupData])

    const isSameSector = useMemo(() => {
        // Para grupos, se não temos group_info ainda, assumir true (validação no backend)
        if (!groupData?.group_info) return true
        return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === groupData.group_info.u_location?.value)[0]?.sectors.filter((i) => i.sys_id === groupData.group_info.u_sector?.value).length > 0
    }, [currentUser, groupData])

    const loggedInUserIsResponsibleFromGroup = useMemo(() => {
        // Para grupos, se não temos group_info ainda, assumir true (validação no backend)
        if (!groupData?.group_info) return true
        return currentUser?.sys_id && currentUser?.sys_id === groupData.group_info.u_notifica_user?.value
    }, [currentUser, groupData])

    // Carregando o diagrama da PRIMEIRA notificação do grupo
    useEffect(() => {
        if (groupData?.notifications?.length > 0) {
            const firstNotificationId = groupData.notifications[0]?.sys_id?.value
            if (firstNotificationId) {
                NotificationActions.getIshikawaByNotificationId(notificationDispatch, firstNotificationId)
            }
        }
    }, [currentUser, groupData])

    useEffect(() => {
        let doNotPerformOptionalIsChecked = toBoolean(ishikawaDiagramUseMemo?.do_not_perform_optional)
        if (doNotPerformOptionalIsChecked) {
            ishikawaDiagramUseMemo?.categories?.forEach(e => e.causes = [])
        }
        setIsDisabled(doNotPerformOptionalIsChecked)
    }, [ishikawaDiagramUseMemo])

    useEffect(() => {
        const allowedProfiles = canEditIshikawaPerfil({ loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState })
        const hasPermission = allowedProfiles.includes(currentUser?.profile)

        if (hasPermission && !statusIsArchivedOrFinished) {
            setNotAllowed(false)
        } else {
            setNotAllowed(true)
        }
    }, [statusIsArchivedOrFinished, loggedInUserIsResponsible, isSameLocation, isSameSector, ishikawaDiagramUseMemo, currentUser?.profile, notificationStatusState])

    // Redireciona para o form de edição em grupo
    const formIshikawa = () => {
        navigate(`${ROUTES_NAMES.notifications}/group_ishikawa/${id}`)
    }

    const onChangeCheckedNotPerform = async (checkedValue: boolean) => {
        try {
            if (!groupData || !currentUser) {
                console.error('Dados do grupo ou usuário não encontrados')
                return
            }

            const ishikawaIdByNotif = mergeFirstIshikawaIdFromRedux(
                groupData,
                await buildIshikawaSysIdByNotificationMap(groupData),
                notificationSelector.ishikawaDiagram
            )
            const { withProtocol: withDiagram, withoutProtocol: withoutDiagram } =
                partitionNotificationsByProtocolMap(groupData as any, ishikawaIdByNotif)

            let template: Record<string, any> =
                normalizeIshikawaApiResponseBody(notificationSelector.ishikawaDiagram) ||
                (notificationSelector.ishikawaDiagram as Record<string, any>) ||
                {}
            if (withDiagram.length > 0 && Object.keys(template).length === 0) {
                const firstId = groupData.notifications[0]?.sys_id?.value
                if (firstId) {
                    const row = await NotificationService.getIshikawaRowByNotificationId(firstId)
                    if (row) template = row
                }
            }

            if (withDiagram.length > 0) {
                await NotificationActions.updateIshikawaBatch(
                    notificationDispatch,
                    convertNotPerformIshikawaUpdateBatchRequest(
                        withDiagram as GroupNotificationItem[],
                        template,
                        checkedValue,
                        ishikawaIdByNotif,
                        currentUser,
                        groupData
                    )
                )
            }
            if (withoutDiagram.length > 0) {
                await NotificationActions.createIshikawaBatch(
                    notificationDispatch,
                    convertNotPerformIshikawaCreateBatchForNotifications(
                        withoutDiagram as GroupNotificationItem[],
                        groupData,
                        currentUser,
                        checkedValue
                    )
                )
            }

            if (groupData?.notifications?.length > 0) {
                const firstNotificationId = groupData.notifications[0]?.sys_id?.value
                if (firstNotificationId) {
                    NotificationActions.getIshikawaByNotificationId(
                        notificationDispatch,
                        firstNotificationId
                    )
                }
            }
        } catch (error) {
            console.error('Erro ao alterar status "não realizar" em massa:', error)
        }
    }

    // Para grupos, o Ishikawa é sempre obrigatório, então não mostrar checkbox
    const hideOrShowCheckUnrealizedOptional = false

    const isIshikawaMandatory = useMemo(() => {
        return calculateGroupIshikawaMandatory(firstNotification)
    }, [firstNotification])

    return (
        <div data-testid="test-group-ishikawa-diagram">
            <TitleIshikawaDiagram>
                Análise do Diagrama de Ishikawa
            </TitleIshikawaDiagram>
            <div className="px-8 flex flex-row gap-12 mt-6 mb-1">
                Ao preencher este diagrama, visualizamos a causa real do
                Incidente ou Evento adverso. O seu objetivo é identificar e
                classificar os principais fatores que levaram o problema (efeito
                inesperado) acontecer.
            </div>
            <div className="px-8 flex flex-row gap-12 mt-1 mb-10 text-[14px] text-gray-500">
                Preenchimento obrigatório para todas notificações, exceto as
                graves e óbitos (facultativas).
            </div>
            <fieldset disabled={notAllowed}>
                <div className="px-8 flex flex-row gap-12 mt-6 mb-10">
                    <EditButton
                        type="button"
                        disabled={isDisabled || notAllowed}
                        onClick={formIshikawa}
                        className={`${isDisabled || notAllowed ? 'bg-disabled text-textSelect font-normal' : ''}`}
                    >
                        <div className="w-[202px] flex justify-center items-center gap-3">
                            <MdOutlineModeEditOutline size={18} color={`${!isDisabled || notAllowed ? 'white' : '#4F4F4F'}`} />
                            <span className="inline-block align-middle">
                                {!getIshikawaRecordId(ishikawaDiagramUseMemo) ? 'Preencher diagrama' : 'Editar diagrama'}
                            </span>
                        </div>
                    </EditButton>

                    {hideOrShowCheckUnrealizedOptional && (
                        <div className="mt-2 block min-h-[1.5rem] pl-[1.5rem]">
                            <input
                                onChange={(e) => {
                                    const isChecked = e.target.checked
                                    onChangeCheckedNotPerform(isChecked)
                                }}
                                checked={isDisabled}
                                name='do_not_perform_optional'
                                className={`${checkInput} ${notAllowed ? "hover:cursor-default" : "hover:cursor-pointer"}`}
                                type="checkbox"
                                id="checkUnrealizedOptionalGroup"
                            />
                            <label
                                className={`inline-block pl-[0.15rem] ${notAllowed ? "opacity-40" : "hover:cursor-pointer"}`}
                                htmlFor="checkUnrealizedOptionalGroup"
                            >
                                Não realizar o preenchimento para o grupo
                            </label>
                        </div>
                    )}
                </div>

                <div className="px-8 m-0">
                    <Ishikawa data={ishikawaDiagramUseMemo} />
                </div>
            </fieldset>
        </div>
    )
}

export { GroupIshikawaDiagram }